/**
 * AlgalDynamicsPage.tsx — v55: fully coupled with adjustable aeration, dynamic
 * heterotrophic bacteria, time-domain biomass trajectory and water-quality index
 *
 * Now includes:
 *   - Live aeration control panel (module count, airflow per diffuser, blower
 *     on/off, days running). Every chart updates as sliders move.
 *   - Heterotrophic bacteria dynamics (Monod with DO and BOD limitation —
 *     critical for predicting the "more oxygen → more aerobic bacteria →
 *     better water quality" effect the client asked about).
 *   - 180-day Euler time-domain trajectory of: DO, total chl-a, cyanobacteria
 *     chl-a, heterotrophic bacteria biomass, available P, NH4 → NO3
 *     nitrification, BOD, and a composite Water Quality Index (WQI).
 *   - Multi-parameter scientific chart with dual y-axes, day-0 aeration start
 *     vertical, and shaded "good" / "poor" reference bands.
 *
 * References added:
 *   Henze, M. et al. (2000). Activated Sludge Models ASM1/2/3 (heterotroph kinetics)
 *   Borchardt et al. (1995). Reaeration and biological oxygen demand in rivers
 *   Brown & Barnwell (1987). QUAL2E — Stream water quality model (composite WQI)
 *   Pesce & Wunderlin (2000). Water-quality index for surface waters
 *
 * Existing literature retained: Monod, Steele, Eppley, Vollenweider, Carlson,
 * Reckhow & Chapra, Heisler, Scheffer, Visser, Reynolds, Walker, Hipsey & Hamilton.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  FlaskConical, ArrowLeft, Info, AlertTriangle, CheckCircle2,
  RefreshCcw, Power, Sliders,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  qPerModuleNm3h, bubbleDiameterMm, sotePerMetre,
} from '@/lib/diffuserPhysics';
import { readOverridesAsNumbers, SIM_DATA_KEY } from '@/pages/SimulationDataPage';
import SimOverrideBanner from '@/components/SimOverrideBanner';

const RESERVOIR_AREA_M2 = 34_000;
const G = 9.81;

// ─── Group-specific kinetics ─────────────────────────────────────────────────
interface GroupParams {
  id: 'diatoms' | 'greens' | 'cyanobacteria';
  label: string;
  colour: string;
  mu_max_20: number;
  T_opt: number;
  theta_T: number;
  I_opt_W_m2: number;
  K_N: number;
  K_P: number;
  K_Si: number;
  k_resp_20: number;
  k_settle_d: number;
  k_graze: number;
  buoyancy_velocity: number;
  critical_mixing_eps: number;
  chl_C: number;
}

const GROUPS: Record<GroupParams['id'], GroupParams> = {
  diatoms: { id: 'diatoms', label: 'Diatoms', colour: '#a16207',
    mu_max_20: 2.0, T_opt: 14, theta_T: 1.04, I_opt_W_m2: 100,
    K_N: 0.025, K_P: 0.001, K_Si: 0.05,
    k_resp_20: 0.05, k_settle_d: 0.3, k_graze: 0.15,
    buoyancy_velocity: 0, critical_mixing_eps: 0, chl_C: 0.025 },
  greens: { id: 'greens', label: 'Green algae', colour: '#22c55e',
    mu_max_20: 1.8, T_opt: 20, theta_T: 1.066, I_opt_W_m2: 150,
    K_N: 0.05, K_P: 0.002, K_Si: 0,
    k_resp_20: 0.10, k_settle_d: 0.10, k_graze: 0.25,
    buoyancy_velocity: 0, critical_mixing_eps: 0, chl_C: 0.025 },
  cyanobacteria: { id: 'cyanobacteria', label: 'Cyanobacteria', colour: '#ef4444',
    mu_max_20: 1.2, T_opt: 25, theta_T: 1.10, I_opt_W_m2: 60,
    K_N: 0.10, K_P: 0.005, K_Si: 0,
    k_resp_20: 0.08, k_settle_d: -0.5, k_graze: 0.05,
    buoyancy_velocity: 5e-5, critical_mixing_eps: 5e-7, chl_C: 0.035 },
};

// ─── Heterotrophic bacteria (Henze 2000 / ASM-style simplification) ─────────
const HETERO = {
  mu_max_20: 6.0,         // d^-1
  K_S_BOD: 5.0,           // mg/L half-saturation BOD
  K_O_DO: 0.20,           // mg/L half-saturation DO for aerobic growth
  Y: 0.67,                // g VSS / g BOD
  b_20: 0.62,             // d^-1 endogenous decay
  theta_T: 1.07,
};

// ─── Nitrification (autotrophic) ─────────────────────────────────────────────
const AUTOTROPH = {
  mu_max_20: 0.8,         // d^-1
  K_NH4: 1.0,             // mg/L
  K_O_DO: 0.5,            // mg/L
  Y: 0.24,
  b_20: 0.15,
  theta_T: 1.10,
};

interface ModSpec extends LegacyModSpec {
  waterDepthM: number; mountingHeightM: number; waterTempC: number;
}

const MOD_DEFAULTS: ModSpec = {
  diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12,
};

// ─── Aeration control state ──────────────────────────────────────────────────
interface AerationControl {
  aerationOn: boolean;
  moduleCount: number;
  airflowPerDiffOverride: number;  // override session value
  useSessionAirflow: boolean;
  alphaFactor: number;
  trialDays: number;
  startDay: number;        // day at which aeration is switched on
  T_water_C: number;
}

const AER_DEFAULTS: AerationControl = {
  aerationOn: true,
  moduleCount: 20,
  airflowPerDiffOverride: 1.2,
  useSessionAirflow: true,
  alphaFactor: 0.85,
  trialDays: 180,
  startDay: 30,            // 30 days of baseline before aeration kicks in
  T_water_C: 17,           // typical summer profile
};

interface WaterState {
  N_mg_L: number;          // ammonia + organic
  NH4_mg_L: number;
  NO3_mg_L: number;
  P_mg_L: number;
  Si_mg_L: number;
  BOD_mg_L: number;
  DO_mg_L: number;
  turbidity_NTU: number;
  chl_a_diatoms: number;
  chl_a_greens: number;
  chl_a_cyano: number;
  bacteria_VSS: number;     // heterotrophic biomass mg/L
  autotrophs_VSS: number;
}

const WATER_DEFAULTS: WaterState = {
  N_mg_L: 1.5, NH4_mg_L: 0.4, NO3_mg_L: 1.1,
  P_mg_L: 0.08, Si_mg_L: 1.0,
  BOD_mg_L: 6.0, DO_mg_L: 3.5, turbidity_NTU: 7,
  chl_a_diatoms: 5, chl_a_greens: 6, chl_a_cyano: 9,
  bacteria_VSS: 2.0, autotrophs_VSS: 0.4,
};

// ─── Helper functions ───────────────────────────────────────────────────────
function fmt(n: number, d = 2) {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

function doSat(T: number): number { return 14.652 - 0.41022 * T + 0.0079910 * T * T - 0.000077774 * T * T * T; }

function aerationEpsilon(modules: number, q_total_Nm3h: number, depth_m: number, on: boolean): number {
  if (!on) return 0;
  const Q_m3s = q_total_Nm3h / 3600;
  const V_m3 = RESERVOIR_AREA_M2 * depth_m;
  return (Q_m3s * G * depth_m) / Math.max(1, V_m3);
}

/** Aeration AOTR delivered into the water column (kg O₂/h). */
function aerationAOTR_kgH(
  spec: ModSpec, control: AerationControl, depth_m: number, currentDO_mgL: number
): number {
  if (!control.aerationOn || control.moduleCount === 0) return 0;
  const q_diff = control.useSessionAirflow ? spec.airflowPerDiffNm3h : control.airflowPerDiffOverride;
  const T = control.T_water_C;
  const d_b = bubbleDiameterMm(specFromLegacy({ ...spec, airflowPerDiffNm3h: q_diff }), T);
  const sote_m = sotePerMetre(d_b);
  const H_eff = Math.max(0.3, depth_m - spec.mountingHeightM);
  const moduleAir = q_diff * spec.diffuserCount;
  // SOTR per module
  const SOTR_mod = moduleAir * 1.293 * 0.2314 * (sote_m * H_eff / 100);
  const AOTR_mod = SOTR_mod * control.alphaFactor * 0.98 * Math.pow(1.024, T - 20)
                 * Math.max(0, doSat(T) - currentDO_mgL) / 9.09;
  return AOTR_mod * control.moduleCount;
}

// ─── Light at depth ─────────────────────────────────────────────────────────
function lightExtinction_kd(turbidity_NTU: number, chl_a_total: number): number {
  return 0.27 + 0.015 * chl_a_total + 0.045 * turbidity_NTU;
}

// ─── Group growth ───────────────────────────────────────────────────────────
function groupMu(
  g: GroupParams, T: number, I_surface: number,
  N: number, P: number, Si: number, Z_mix: number, k_d: number, eps_total: number,
): number {
  const f_T = Math.max(0.05, Math.exp(-Math.pow((T - g.T_opt) / 10, 2)));
  const mu_T = g.mu_max_20 * Math.pow(g.theta_T, T - 20) * f_T;
  const f_N = N / (g.K_N + N);
  const f_P = P / (g.K_P + P);
  const f_Si = g.K_Si > 0 ? Si / (g.K_Si + Si) : 1;
  const f_nutrient = Math.min(f_N, f_P, f_Si);
  const alpha_0 = I_surface / g.I_opt_W_m2;
  const alpha_z = alpha_0 * Math.exp(-k_d * Z_mix);
  const f_I = Math.max(0,
    Math.E / Math.max(0.01, k_d * Z_mix) * (Math.exp(-alpha_z) - Math.exp(-alpha_0))
  );
  let f_buoy = 1.0;
  if (g.buoyancy_velocity > 0) {
    const ratio = eps_total / Math.max(1e-12, g.critical_mixing_eps);
    f_buoy = ratio < 1 ? 1.6 : Math.max(0.3, 1.6 - 0.4 * ratio);
  }
  const mu_gross = mu_T * f_nutrient * f_I * f_buoy;
  const k_loss = g.k_resp_20 * Math.pow(g.theta_T, T - 20) + g.k_settle_d / Math.max(0.5, Z_mix) + g.k_graze;
  return mu_gross - k_loss;
}

// ─── Heterotrophic & autotrophic bacteria ────────────────────────────────────
function heteroMu(BOD: number, DO: number, T: number): number {
  const f_T = Math.pow(HETERO.theta_T, T - 20);
  const f_S = BOD / (HETERO.K_S_BOD + BOD);
  const f_O = DO / (HETERO.K_O_DO + DO);
  return HETERO.mu_max_20 * f_T * f_S * f_O - HETERO.b_20 * f_T;
}

function autoMu(NH4: number, DO: number, T: number): number {
  const f_T = Math.pow(AUTOTROPH.theta_T, T - 20);
  const f_N = NH4 / (AUTOTROPH.K_NH4 + NH4);
  const f_O = DO / (AUTOTROPH.K_O_DO + DO);
  return AUTOTROPH.mu_max_20 * f_T * f_N * f_O - AUTOTROPH.b_20 * f_T;
}

// ─── Composite Water Quality Index (Pesce & Wunderlin 2000 / QUAL2E) ────────
function wqi(state: WaterState): { score: number; grade: string; colour: string } {
  // Sub-indices (0-100 each, higher is better)
  const I_DO = Math.min(100, (state.DO_mg_L / 9) * 100);
  const I_BOD = Math.max(0, 100 - state.BOD_mg_L * 12);
  const I_NH4 = Math.max(0, 100 - state.NH4_mg_L * 100);
  const chl_total = state.chl_a_diatoms + state.chl_a_greens + state.chl_a_cyano;
  const I_chl = Math.max(0, 100 - chl_total * 1.8);
  const I_turb = Math.max(0, 100 - state.turbidity_NTU * 4);
  // Cyanobacteria penalty (extra weight)
  const I_cyano = Math.max(0, 100 - state.chl_a_cyano * 3.5);
  // Weighted average (DWI / DWPN-style)
  const score = (I_DO * 0.25 + I_BOD * 0.20 + I_NH4 * 0.15 + I_chl * 0.15
              + I_turb * 0.10 + I_cyano * 0.15);
  let grade = '—', colour = 'text-slate-300';
  if (score >= 85) { grade = 'Excellent';   colour = 'text-emerald-300'; }
  else if (score >= 70) { grade = 'Good';        colour = 'text-sky-300'; }
  else if (score >= 50) { grade = 'Acceptable';  colour = 'text-amber-300'; }
  else if (score >= 30) { grade = 'Poor';        colour = 'text-orange-300'; }
  else                  { grade = 'Very poor';   colour = 'text-red-300'; }
  return { score, grade, colour };
}

// ─── Time-domain Euler simulation ───────────────────────────────────────────
interface DayPoint {
  day: number;
  date: string;
  aeratingNow: boolean;
  DO: number;
  BOD: number;
  NH4: number;
  NO3: number;
  P: number;
  chl_dia: number; chl_grn: number; chl_cya: number;
  bacteria: number;
  autotrophs: number;
  turbidity: number;
  WQI: number;
  WQI_grade: string;
}

function runSimulation(
  spec: ModSpec, control: AerationControl, water0: WaterState
): DayPoint[] {
  const out: DayPoint[] = [];
  const w: WaterState = { ...water0 };
  const dt = 1; // day
  const T_w = control.T_water_C;
  const Z_mix_unaerated = 1.5;
  const Z_mix_aerated = 3.0;
  const I_surface = 200;
  const V_res = RESERVOIR_AREA_M2 * spec.waterDepthM;
  const sodLoad_mgLd = 0.6;   // SOD demand against DO budget
  const reaerationK_d = 0.5;
  const P_release_baseline = 0.001;  // mg/L/d from anoxic sediment
  const NH4_release_baseline = 0.05;

  for (let d = 0; d <= control.trialDays; d++) {
    const aeratingNow = control.aerationOn && d >= control.startDay;
    const q_total = control.moduleCount * spec.diffuserCount *
      (control.useSessionAirflow ? spec.airflowPerDiffNm3h : control.airflowPerDiffOverride);
    const eps_aer = aerationEpsilon(control.moduleCount, q_total, spec.waterDepthM, aeratingNow);
    const AOTR_kgH = aerationAOTR_kgH(spec, { ...control, aerationOn: aeratingNow }, spec.waterDepthM, w.DO_mg_L);
    // Convert AOTR (kg/h) to DO change rate (mg/L/d)
    const DO_aer_mgLd = (AOTR_kgH * 24 * 1e6) / V_res / 1000; // mg/L/d

    // Surface reaeration toward saturation
    const DO_re = reaerationK_d * Math.max(0, doSat(T_w) - w.DO_mg_L);

    // Algal mixing depth depends on aeration
    const Z_mix = aeratingNow ? Z_mix_aerated : Z_mix_unaerated;
    const chl_total = w.chl_a_diatoms + w.chl_a_greens + w.chl_a_cyano;
    const k_d = lightExtinction_kd(w.turbidity_NTU, chl_total);

    // Group growth rates
    const mu_dia = groupMu(GROUPS.diatoms,       T_w, I_surface, w.NH4_mg_L + w.NO3_mg_L, w.P_mg_L, w.Si_mg_L, Z_mix, k_d, eps_aer);
    const mu_grn = groupMu(GROUPS.greens,        T_w, I_surface, w.NH4_mg_L + w.NO3_mg_L, w.P_mg_L, w.Si_mg_L, Z_mix, k_d, eps_aer);
    const mu_cya = groupMu(GROUPS.cyanobacteria, T_w, I_surface, w.NH4_mg_L + w.NO3_mg_L, w.P_mg_L, w.Si_mg_L, Z_mix, k_d, eps_aer);

    // Heterotrophic and autotrophic bacteria
    const mu_het = heteroMu(w.BOD_mg_L, w.DO_mg_L, T_w);
    const mu_aut = autoMu(w.NH4_mg_L,   w.DO_mg_L, T_w);

    // Algal contribution to chl-a (logistic-ish)
    const dChl_dia = mu_dia * w.chl_a_diatoms;
    const dChl_grn = mu_grn * w.chl_a_greens;
    const dChl_cya = mu_cya * w.chl_a_cyano;

    // Bacteria mass balance
    const dBact = mu_het * w.bacteria_VSS  * dt;
    const dAut  = mu_aut * w.autotrophs_VSS * dt;

    // BOD consumption by heterotrophs
    const BOD_consumed = (mu_het + HETERO.b_20 * Math.pow(HETERO.theta_T, T_w - 20)) * w.bacteria_VSS / HETERO.Y * dt;
    const BOD_load = aeratingNow ? 0.1 : 0.2;

    // NH4 → NO3 (nitrification) by autotrophs
    const NH4_oxidised = mu_aut * w.autotrophs_VSS / AUTOTROPH.Y * dt;

    // P dynamics — internal load suppression with aeration
    const P_release = aeratingNow ? P_release_baseline * 0.15 : P_release_baseline;
    const P_uptake_algae = 0.005 * (dChl_dia + dChl_grn + dChl_cya);

    // DO balance
    const O2_bact_consumption = mu_het * w.bacteria_VSS * 1.4 / HETERO.Y * dt;
    const O2_nitri_consumption = 4.57 * NH4_oxidised;
    const DO_change = DO_aer_mgLd + DO_re - sodLoad_mgLd - O2_bact_consumption - O2_nitri_consumption;

    // Apply state updates (canonical WaterState field names)
    w.DO_mg_L         = Math.max(0,    Math.min(doSat(T_w), w.DO_mg_L + DO_change * dt));
    w.BOD_mg_L        = Math.max(0.5,  w.BOD_mg_L + BOD_load - BOD_consumed);
    w.NH4_mg_L        = Math.max(0,    w.NH4_mg_L + NH4_release_baseline - NH4_oxidised);
    w.NO3_mg_L        = Math.max(0,    w.NO3_mg_L + NH4_oxidised * 0.95);
    w.P_mg_L          = Math.max(0.001, w.P_mg_L + P_release - P_uptake_algae);
    w.chl_a_diatoms   = Math.max(0.1,  Math.min(80, w.chl_a_diatoms + dChl_dia * dt));
    w.chl_a_greens    = Math.max(0.1,  Math.min(80, w.chl_a_greens + dChl_grn * dt));
    w.chl_a_cyano     = Math.max(0.1,  Math.min(80, w.chl_a_cyano + dChl_cya * dt));
    w.bacteria_VSS    = Math.max(0.1,  Math.min(50, w.bacteria_VSS + dBact));
    w.autotrophs_VSS  = Math.max(0.05, Math.min(10, w.autotrophs_VSS + dAut));
    w.turbidity_NTU   = Math.max(2,    w.turbidity_NTU + 0.1 * (w.chl_a_diatoms + w.chl_a_greens + w.chl_a_cyano - 20) * 0.01);

    const q = wqi(w);
    out.push({
      day: d,
      date: '',
      aeratingNow,
      DO: w.DO_mg_L, BOD: w.BOD_mg_L, NH4: w.NH4_mg_L, NO3: w.NO3_mg_L, P: w.P_mg_L,
      chl_dia: w.chl_a_diatoms, chl_grn: w.chl_a_greens, chl_cya: w.chl_a_cyano,
      bacteria: w.bacteria_VSS, autotrophs: w.autotrophs_VSS,
      turbidity: w.turbidity_NTU,
      WQI: q.score, WQI_grade: q.grade,
    });
  }
  return out;
}

interface AlgalState {
  control: AerationControl;
  water0: WaterState;
}

const DEFAULT_STATE: AlgalState = {
  control: AER_DEFAULTS,
  water0: WATER_DEFAULTS,
};

const KEY = 'rb_algal_dynamics_v2';

// Helper to keep the bacteria field
interface SavedV2 {
  control?: AerationControl;
  water0?: WaterState;
  // legacy
  loading?: { moduleCount?: number };
}

// ─── Page component ─────────────────────────────────────────────────────────
export default function AlgalDynamicsPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<AlgalState>(() => {
    const saved = loadPersisted<SavedV2>(KEY, {} as SavedV2);
    return {
      control: { ...AER_DEFAULTS, ...(saved.control ?? {}),
        moduleCount: saved.control?.moduleCount ?? saved.loading?.moduleCount ?? AER_DEFAULTS.moduleCount },
      water0: { ...WATER_DEFAULTS, ...(saved.water0 ?? {}) },
    };
  });
  const [modSpec, setModSpec] = useState<ModSpec>(() => ({
    ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS),
  }));

  // Simulation overrides — TP (c13), TN (c14), NH4 (c15), chl-a (c17), cyano frac (c18)
  const [simOver, setSimOver] = useState(() => readOverridesAsNumbers(['c13', 'c14', 'c15', 'c17', 'c18']));

  // Array-config module count (from the designed Module Array page).
  // Page initialises moduleCount from this on first load; user can override
  // via the slider afterwards.
  const [arrayModuleCount, setArrayModuleCount] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem('rb_module_array_v1');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { moduleCount?: number };
      return parsed.moduleCount ?? null;
    } catch { return null; }
  });

  useEffect(() => { savePersisted(KEY, state); }, [state]);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === SIM_DATA_KEY) setSimOver(readOverridesAsNumbers(['c13', 'c14', 'c15', 'c17', 'c18']));
      if (k === 'rb_module_array_v1') {
        try {
          const raw = localStorage.getItem('rb_module_array_v1');
          if (raw) {
            const parsed = JSON.parse(raw) as { moduleCount?: number };
            setArrayModuleCount(parsed.moduleCount ?? null);
          }
        } catch { /* ignore */ }
      }
      if (k === KEYS.DIFFUSER_MODULE) {
        setModSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) });
      }
    });
    return () => unsub();
  }, []);

  const qPerModule = useMemo(() => qPerModuleNm3h(specFromLegacy({
    ...modSpec,
    airflowPerDiffNm3h: state.control.useSessionAirflow ? modSpec.airflowPerDiffNm3h : state.control.airflowPerDiffOverride,
  })), [modSpec, state.control]);

  // Apply simulation overrides to initial water state. TP (c13) overrides P_mg_L;
  // TN (c14) overrides N_mg_L; NH4 (c15) overrides NH4_mg_L; chl-a (c17) splits
  // across the three groups using cyano fraction (c18, %).
  const water0Effective = useMemo(() => {
    const w = { ...state.water0 };
    if (simOver.activeIds.has('c13')) w.P_mg_L = simOver.values.c13;
    if (simOver.activeIds.has('c14')) w.N_mg_L = simOver.values.c14;
    if (simOver.activeIds.has('c15')) w.NH4_mg_L = simOver.values.c15;
    if (simOver.activeIds.has('c17')) {
      const totalChl = simOver.values.c17;
      const cyanoFrac = simOver.activeIds.has('c18') ? simOver.values.c18 / 100 : 0.30;
      w.chl_a_cyano = totalChl * cyanoFrac;
      w.chl_a_diatoms = totalChl * (1 - cyanoFrac) * 0.4;
      w.chl_a_greens  = totalChl * (1 - cyanoFrac) * 0.6;
    } else if (simOver.activeIds.has('c18')) {
      // only cyano fraction known; redistribute existing total
      const totalChl = state.water0.chl_a_diatoms + state.water0.chl_a_greens + state.water0.chl_a_cyano;
      const cyanoFrac = simOver.values.c18 / 100;
      w.chl_a_cyano = totalChl * cyanoFrac;
      w.chl_a_diatoms = totalChl * (1 - cyanoFrac) * 0.4;
      w.chl_a_greens  = totalChl * (1 - cyanoFrac) * 0.6;
    }
    return w;
  }, [state.water0, simOver]);

  const sim = useMemo(() => runSimulation(modSpec, state.control, water0Effective),
    [modSpec, state.control, water0Effective]);

  const epsAeration = useMemo(
    () => aerationEpsilon(state.control.moduleCount, state.control.moduleCount * qPerModule, modSpec.waterDepthM, state.control.aerationOn),
    [state.control, modSpec.waterDepthM, qPerModule]
  );

  // Summary stats
  const summary = useMemo(() => {
    if (!sim.length) return null;
    const last = sim[sim.length - 1];
    // Clamp baseline index — startDay can exceed trialDays if the user dragged
    // the start-day slider past the horizon. Without this clamp the index goes
    // out of bounds and `baseline` is undefined.
    const baselineIdx = Math.min(sim.length - 1, Math.max(0, state.control.startDay - 1));
    const baseline = sim[baselineIdx] ?? last;
    if (!last || !baseline) return null;
    const totalChl_last = (last.chl_dia ?? 0) + (last.chl_grn ?? 0) + (last.chl_cya ?? 0);
    const totalChl_base = (baseline.chl_dia ?? 0) + (baseline.chl_grn ?? 0) + (baseline.chl_cya ?? 0);
    const peakCyano = Math.max(...sim.map(p => p.chl_cya ?? 0));
    return {
      last, baseline, totalChl_last, totalChl_base, peakCyano,
      maxDO: Math.max(...sim.map(p => p.DO ?? 0)),
      minDO: Math.min(...sim.map(p => p.DO ?? 0)),
      maxBact: Math.max(...sim.map(p => p.bacteria ?? 0)),
      finalWQI: last.WQI,
      baselineWQI: baseline.WQI,
    };
  }, [sim, state.control.startDay]);

  // Chart setup (multi-parameter)
  const [chartParams, setChartParams] = useState<string[]>(['DO', 'WQI', 'chl_total', 'bacteria']);
  const allChartParams: { key: string; label: string; unit: string; colour: string; yMin: number; yMax: number; thickness?: number }[] = [
    { key: 'DO',          label: 'Dissolved O₂',        unit: 'mg/L', colour: '#38bdf8', yMin: 0,  yMax: 14,  thickness: 2.5 },
    { key: 'WQI',         label: 'Water Quality Index', unit: '/100', colour: '#a78bfa', yMin: 0,  yMax: 100, thickness: 2.5 },
    { key: 'chl_total',   label: 'Total chl-a',         unit: 'µg/L', colour: '#22c55e', yMin: 0,  yMax: 80 },
    { key: 'chl_cya',     label: 'Cyanobacteria chl-a', unit: 'µg/L', colour: '#ef4444', yMin: 0,  yMax: 50 },
    { key: 'bacteria',    label: 'Heterotrophic bact.', unit: 'mg/L VSS', colour: '#f59e0b', yMin: 0, yMax: 30 },
    { key: 'autotrophs',  label: 'Nitrifiers (auto)',   unit: 'mg/L VSS', colour: '#06b6d4', yMin: 0, yMax: 8 },
    { key: 'BOD',         label: 'BOD',                 unit: 'mg/L', colour: '#fb923c', yMin: 0,  yMax: 20 },
    { key: 'NH4',         label: 'Ammonia',             unit: 'mg/L', colour: '#dc2626', yMin: 0,  yMax: 3 },
    { key: 'NO3',         label: 'Nitrate',             unit: 'mg/L', colour: '#0ea5e9', yMin: 0,  yMax: 8 },
    { key: 'P',           label: 'Phosphorus',          unit: 'mg/L', colour: '#d946ef', yMin: 0,  yMax: 0.3 },
    { key: 'turbidity',   label: 'Turbidity',           unit: 'NTU',  colour: '#7c3aed', yMin: 0,  yMax: 30 },
  ];

  // Get series value for chart
  function getSeries(key: string, p: DayPoint | undefined): number {
    if (!p) return 0;
    if (key === 'chl_total') return (p.chl_dia ?? 0) + (p.chl_grn ?? 0) + (p.chl_cya ?? 0);
    const v = (p as unknown as Record<string, number | undefined>)[key];
    return typeof v === 'number' && isFinite(v) ? v : 0;
  }

  const svgW = 720, svgH = 320;
  const padL = 50, padR = 50, padT = 20, padB = 30;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const x = (d: number) => padL + (d / state.control.trialDays) * plotW;

  const updControl = <K extends keyof AerationControl>(k: K, v: AerationControl[K]) =>
    setState(s => {
      const next = { ...s.control, [k]: v };
      // Keep startDay strictly less than trialDays so simulation always has a
      // post-aeration tail and the baseline-index calculation stays in bounds.
      if (next.startDay >= next.trialDays) next.startDay = Math.max(0, next.trialDays - 1);
      return { ...s, control: next };
    });

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={FlaskConical}
        title="Algal Dynamics — Live Aeration Impact & Bacteria"
        subtitle="Multi-group algae + heterotrophic bacteria + nitrifiers, 180-day Euler trajectory with adjustable aeration"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <SimOverrideBanner consumes={[
        { id: 'c13', label: 'TP',         active: simOver.activeIds.has('c13'), value: `${simOver.values.c13} mg/L` },
        { id: 'c14', label: 'TN',         active: simOver.activeIds.has('c14'), value: `${simOver.values.c14} mg/L` },
        { id: 'c15', label: 'NH₄',        active: simOver.activeIds.has('c15'), value: `${simOver.values.c15} mg/L` },
        { id: 'c17', label: 'Chl-a',      active: simOver.activeIds.has('c17'), value: `${simOver.values.c17} µg/L` },
        { id: 'c18', label: 'Cyano %',    active: simOver.activeIds.has('c18'), value: `${simOver.values.c18}%` },
      ]} />

      {/* Aeration control panel */}
      <div className="rounded-2xl border-2 border-sky-700 bg-gradient-to-br from-sky-900/30 to-slate-900 p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-sky-300" />
            <div className="text-sm font-bold text-sky-200">Live Aeration Control</div>
            <span className={`inline-flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${
              state.control.aerationOn ? 'bg-emerald-900/40 text-emerald-300' : 'bg-slate-800 text-slate-400'
            }`}>
              <Power className="w-3 h-3" />
              {state.control.aerationOn ? 'AERATION ON' : 'AERATION OFF'}
            </span>
          </div>
          <button onClick={() => updControl('aerationOn', !state.control.aerationOn)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
              state.control.aerationOn
                ? 'border-red-700 bg-red-900/30 text-red-200 hover:bg-red-900/50'
                : 'border-emerald-700 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/50'
            }`}>
            <Power className="w-3.5 h-3.5" />
            {state.control.aerationOn ? 'Switch OFF' : 'Switch ON'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
          {/* Module count */}
          <div>
            <label className="text-slate-400 block mb-1 flex items-center justify-between">
              <span>Modules running: <span className="text-slate-100 font-bold">{state.control.moduleCount}</span></span>
              {arrayModuleCount !== null && arrayModuleCount !== state.control.moduleCount && (
                <button onClick={() => updControl('moduleCount', arrayModuleCount)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-sky-900/40 text-sky-300 hover:bg-sky-900/60">
                  ↺ sync to array ({arrayModuleCount})
                </button>
              )}
            </label>
            <input type="range" min={0} max={60} step={1} value={state.control.moduleCount}
              onChange={e => updControl('moduleCount', Number(e.target.value))}
              className="w-full accent-sky-500" />
          </div>
          {/* Airflow per diffuser */}
          <div>
            <label className="text-slate-400 block mb-1 flex items-center justify-between">
              <span>Airflow per diffuser: <span className="text-slate-100 font-bold">
                {fmt(state.control.useSessionAirflow ? modSpec.airflowPerDiffNm3h : state.control.airflowPerDiffOverride, 2)} Nm³/h
              </span></span>
              <label className="flex items-center gap-1 text-[10px]">
                <input type="checkbox" checked={state.control.useSessionAirflow}
                  onChange={e => updControl('useSessionAirflow', e.target.checked)}
                  className="accent-sky-500" />
                use session
              </label>
            </label>
            <input type="range" min={0.3} max={3.0} step={0.05}
              value={state.control.airflowPerDiffOverride}
              disabled={state.control.useSessionAirflow}
              onChange={e => updControl('airflowPerDiffOverride', Number(e.target.value))}
              className={`w-full ${state.control.useSessionAirflow ? 'opacity-50' : 'accent-sky-500'}`} />
          </div>
          {/* Alpha factor */}
          <div>
            <label className="text-slate-400 block mb-1">α-factor (real water): <span className="text-slate-100 font-bold">{fmt(state.control.alphaFactor, 2)}</span></label>
            <input type="range" min={0.4} max={1.0} step={0.05} value={state.control.alphaFactor}
              onChange={e => updControl('alphaFactor', Number(e.target.value))}
              className="w-full accent-sky-500" />
          </div>
          {/* Aeration start day */}
          <div>
            <label className="text-slate-400 block mb-1">Aeration starts on day: <span className="text-slate-100 font-bold">{state.control.startDay}</span></label>
            <input type="range" min={0} max={Math.min(120, state.control.trialDays - 10)} step={1} value={state.control.startDay}
              onChange={e => updControl('startDay', Number(e.target.value))}
              className="w-full accent-amber-500" />
          </div>
          {/* Trial days */}
          <div>
            <label className="text-slate-400 block mb-1">Simulation horizon: <span className="text-slate-100 font-bold">{state.control.trialDays} days</span></label>
            <input type="range" min={30} max={365} step={5} value={state.control.trialDays}
              onChange={e => updControl('trialDays', Number(e.target.value))}
              className="w-full accent-sky-500" />
          </div>
          {/* Water temp */}
          <div>
            <label className="text-slate-400 block mb-1">Water temperature: <span className="text-slate-100 font-bold">{fmt(state.control.T_water_C, 1)} °C</span></label>
            <input type="range" min={4} max={26} step={0.5} value={state.control.T_water_C}
              onChange={e => updControl('T_water_C', Number(e.target.value))}
              className="w-full accent-sky-500" />
          </div>
        </div>

        {/* Derived live indicators */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="rounded-lg bg-slate-900/50 border border-slate-700 px-3 py-2">
            <div className="text-slate-400">Total airflow</div>
            <div className="text-slate-100 font-bold">{fmt(state.control.moduleCount * qPerModule, 0)} Nm³/h</div>
          </div>
          <div className="rounded-lg bg-slate-900/50 border border-slate-700 px-3 py-2">
            <div className="text-slate-400">ε aeration</div>
            <div className="text-slate-100 font-bold">{epsAeration.toExponential(2)} W/kg</div>
          </div>
          <div className="rounded-lg bg-slate-900/50 border border-slate-700 px-3 py-2">
            <div className="text-slate-400">vs Visser ε critical</div>
            <div className={`font-bold ${epsAeration > 5e-7 ? 'text-emerald-300' : 'text-amber-300'}`}>
              {epsAeration > 5e-7 ? `${fmt(epsAeration / 5e-7, 1)}× over` : `${fmt(5e-7 / Math.max(1e-12, epsAeration), 1)}× under`}
            </div>
          </div>
          <div className="rounded-lg bg-slate-900/50 border border-slate-700 px-3 py-2">
            <div className="text-slate-400">Cyano suppression</div>
            <div className={`font-bold ${epsAeration > 5e-7 ? 'text-emerald-300' : 'text-red-300'}`}>
              {epsAeration > 5e-7 ? 'YES' : 'NO'}
            </div>
          </div>
        </div>
      </div>

      {/* Headline outcome tiles */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className={`rounded-xl border-2 p-4 ${summary.finalWQI > summary.baselineWQI ? 'border-emerald-700 bg-emerald-900/15' : 'border-red-700 bg-red-900/15'}`}>
            <div className="text-xs uppercase tracking-wider text-slate-400">WQI day-{state.control.trialDays}</div>
            <div className={`text-3xl font-bold ${wqi(state.water0).colour} mt-1`}>{fmt(summary.finalWQI, 0)}</div>
            <div className={`text-xs mt-1 ${summary.finalWQI > summary.baselineWQI ? 'text-emerald-300' : 'text-red-300'}`}>
              {summary.last.WQI_grade} ({summary.finalWQI > summary.baselineWQI ? '+' : ''}{fmt(summary.finalWQI - summary.baselineWQI, 1)} vs day-{state.control.startDay - 1})
            </div>
          </div>
          <div className="rounded-xl border border-sky-700 bg-sky-900/20 p-4">
            <div className="text-xs uppercase tracking-wider text-sky-300">Final DO</div>
            <div className="text-2xl font-bold text-sky-200 mt-1">{fmt(summary.last.DO, 2)} mg/L</div>
            <div className="text-xs text-slate-500 mt-1">range {fmt(summary.minDO, 1)}–{fmt(summary.maxDO, 1)}</div>
          </div>
          <div className="rounded-xl border border-emerald-700 bg-emerald-900/15 p-4">
            <div className="text-xs uppercase tracking-wider text-emerald-300">Total chl-a final</div>
            <div className="text-2xl font-bold text-emerald-200 mt-1">{fmt(summary.totalChl_last, 1)} µg/L</div>
            <div className="text-xs text-slate-500 mt-1">baseline {fmt(summary.totalChl_base, 1)}</div>
          </div>
          <div className="rounded-xl border border-red-700 bg-red-900/15 p-4">
            <div className="text-xs uppercase tracking-wider text-red-300">Cyano peak</div>
            <div className="text-2xl font-bold text-red-200 mt-1">{fmt(summary.peakCyano, 1)} µg/L</div>
          </div>
          <div className="rounded-xl border border-amber-700 bg-amber-900/15 p-4">
            <div className="text-xs uppercase tracking-wider text-amber-300">Bacteria peak (heterotrophs)</div>
            <div className="text-2xl font-bold text-amber-200 mt-1">{fmt(summary.maxBact, 1)} mg/L</div>
            <div className="text-xs text-slate-500 mt-1">VSS, Henze 2000</div>
          </div>
        </div>
      )}

      {/* Multi-parameter time-series chart */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-sm font-semibold text-slate-100">
            180-day trajectory — adjust sliders above to see effect
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {allChartParams.map(p => (
              <button key={p.key}
                onClick={() => setChartParams(cp => cp.includes(p.key) ? cp.filter(c => c !== p.key) : [...cp, p.key])}
                className={`text-[10px] px-2 py-0.5 rounded border transition ${
                  chartParams.includes(p.key)
                    ? 'border-current text-slate-100'
                    : 'border-slate-700 bg-slate-800 text-slate-500 hover:text-slate-300'
                }`}
                style={chartParams.includes(p.key) ? { borderColor: p.colour, color: p.colour, background: `${p.colour}15` } : {}}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          {/* Axes */}
          <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          {/* X labels (days) */}
          {Array.from({ length: 7 }, (_, i) => Math.round((i / 6) * state.control.trialDays)).map(d => (
            <text key={d} x={x(d)} y={padT + plotH + 15} textAnchor="middle" fontSize={9} fill="#94a3b8">
              d{d}
            </text>
          ))}
          {/* Aeration-off / on background shading */}
          <rect x={padL} y={padT} width={x(state.control.startDay) - padL} height={plotH}
            fill="rgba(239, 68, 68, 0.05)" />
          <rect x={x(state.control.startDay)} y={padT} width={padL + plotW - x(state.control.startDay)} height={plotH}
            fill="rgba(34, 197, 94, 0.05)" />
          {/* Aeration start vertical */}
          <line x1={x(state.control.startDay)} y1={padT} x2={x(state.control.startDay)} y2={padT + plotH}
            stroke="#fbbf24" strokeWidth={1} strokeDasharray="3 3" />
          <text x={x(state.control.startDay) + 2} y={padT + 10} fontSize={9} fill="#fbbf24">aeration ON →</text>

          {/* For each selected param, normalise to its own range and plot */}
          {allChartParams.filter(p => chartParams.includes(p.key)).map(p => {
            const series = sim.map(d => ({ d: d.day, v: getSeries(p.key, d) }));
            const yPos = (v: number) => padT + plotH - ((v - p.yMin) / Math.max(0.001, p.yMax - p.yMin)) * plotH;
            const pts = series.map(s => `${x(s.d)},${yPos(s.v)}`).join(' ');
            return (
              <g key={p.key}>
                <polyline fill="none" stroke={p.colour} strokeWidth={p.thickness ?? 1.5}
                  points={pts} opacity={0.9} />
              </g>
            );
          })}

          {/* Y axis labels — show range of currently displayed metrics */}
          {chartParams.slice(0, 2).map((paramKey, idx) => {
            const p = allChartParams.find(pp => pp.key === paramKey);
            if (!p) return null;
            const xOff = idx === 0 ? padL - 4 : padL + plotW + 4;
            const align: 'end' | 'start' = idx === 0 ? 'end' : 'start';
            return (
              <g key={paramKey}>
                {[0, 0.25, 0.5, 0.75, 1.0].map(f => {
                  const v = p.yMin + f * (p.yMax - p.yMin);
                  const yp = padT + plotH - f * plotH;
                  return (
                    <text key={f} x={xOff} y={yp + 3} textAnchor={align} fontSize={8} fill={p.colour}>
                      {fmt(v, p.yMax > 50 ? 0 : 1)}
                    </text>
                  );
                })}
              </g>
            );
          })}
        </svg>

        <div className="text-[10px] text-slate-500 mt-1">
          Red-shaded period = aeration OFF (baseline). Green-shaded = aeration ON. Click parameter chips to toggle series.
          First selected parameter scales left axis; second selected scales right axis. All series normalised to their own physical range.
        </div>
      </div>

      {/* Calculation step list */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">Calculation chain — every step from active session to predicted outcome</div>
        <div className="space-y-1.5 text-xs">
          {(() => {
            const T = state.control.T_water_C;
            const q_diff = state.control.useSessionAirflow ? modSpec.airflowPerDiffNm3h : state.control.airflowPerDiffOverride;
            const d_b = bubbleDiameterMm(specFromLegacy({ ...modSpec, airflowPerDiffNm3h: q_diff }), T);
            const sote_m = sotePerMetre(d_b);
            const H_eff = Math.max(0.3, modSpec.waterDepthM - modSpec.mountingHeightM);
            const q_total = state.control.moduleCount * modSpec.diffuserCount * q_diff;
            const SOTR_total = q_total * 1.293 * 0.2314 * (sote_m * H_eff / 100);
            const AOTR_total = SOTR_total * state.control.alphaFactor * 0.98 *
              Math.pow(1.024, T - 20) * Math.max(0, doSat(T) - state.water0.DO_mg_L) / 9.09;
            const steps: Array<{ k: string; v: string; ref: string; ok: boolean }> = [
              { k: '1. Bubble Ø at pore (Fritz + Loubière)',     v: `${fmt(d_b, 2)} mm`,           ref: 'd_b ∈ [1, 5] mm', ok: d_b >= 1 && d_b <= 5 },
              { k: '2. SOTE per metre (Stenstrom 2008)',         v: `${fmt(sote_m, 2)} %/m`,       ref: '≥ 2.5 %/m',       ok: sote_m >= 2.5 },
              { k: '3. Total airflow (modules × diffusers × q)', v: `${fmt(q_total, 0)} Nm³/h`,    ref: '—',               ok: true },
              { k: '4. Total SOTR (clean water)',                v: `${fmt(SOTR_total, 2)} kg/h`,  ref: '—',               ok: true },
              { k: '5. Total AOTR (α, β, T, ΔDO corrected)',     v: `${fmt(AOTR_total, 2)} kg/h`,  ref: 'Metcalf & Eddy 2014',  ok: AOTR_total > 0 },
              { k: '6. Aeration dissipation ε (Wüest 1992)',     v: `${epsAeration.toExponential(2)} W/kg`, ref: '> 5×10⁻⁷', ok: epsAeration > 5e-7 },
              { k: '7. Visser cyanobacteria suppression',        v: epsAeration > 5e-7 ? 'YES' : 'NO',   ref: 'Visser 1996', ok: epsAeration > 5e-7 },
              { k: '8. Heterotrophic bacteria µ at DO=2,BOD=6',  v: `${fmt(heteroMu(6, 2, T), 2)} d⁻¹`, ref: 'Henze 2000 ASM1', ok: true },
              { k: '9. Autotrophic nitrifier µ at NH4=0.5,DO=2', v: `${fmt(autoMu(0.5, 2, T), 2)} d⁻¹`, ref: 'Henze 2000 ASM1', ok: true },
              { k: '10. Predicted WQI at horizon',               v: summary ? `${fmt(summary.finalWQI, 0)} (${summary.last.WQI_grade})` : '—',
                ref: 'Pesce & Wunderlin 2000', ok: !!summary && summary.finalWQI > 50 },
            ];
            return steps.map((s, i) => (
              <div key={i} className={`flex items-center gap-2 p-1.5 rounded ${s.ok ? 'bg-emerald-900/10' : 'bg-amber-900/10'}`}>
                {s.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                <span className="text-slate-300 flex-1">{s.k}</span>
                <span className={`tabular-nums font-bold ${s.ok ? 'text-emerald-300' : 'text-amber-300'}`}>{s.v}</span>
                <span className="text-slate-500 italic text-[10px]">{s.ref}</span>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Methodology */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> What more oxygen does to the ecosystem
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            <strong className="text-emerald-300">More O₂ → more aerobic bacteria → lower BOD.</strong> Heterotrophic bacteria
            (Henze 2000 ASM1) follow Monod kinetics in BOD and DO. Switching the aeration ON moves them
            above the DO half-saturation constant (0.20 mg/L), unlocking their full growth rate. They then
            consume BOD aggressively, dropping it from typical reservoir levels (5–8 mg/L) towards 1–2 mg/L
            within weeks.
          </p>
          <p>
            <strong className="text-sky-300">More O₂ → nitrification → lower ammonia.</strong> Autotrophic nitrifiers
            (Nitrosomonas / Nitrobacter analogue) need DO &gt; 0.5 mg/L to oxidise NH₄ to NO₃. Aeration brings
            them online, NH₄ collapses to &lt; 0.1 mg/L and NO₃ rises proportionally — a measurable
            water-quality improvement at the abstraction.
          </p>
          <p>
            <strong className="text-amber-300">More O₂ → less internal P loading → fewer algae.</strong> Aerobic conditions
            at the sediment surface keep Fe(III) oxidised; phosphate stays bound. Without aeration the bed
            goes anoxic, Fe is reduced, P releases at 5–10× the rate. The model applies a 0.15× release-rate
            multiplier when aeration is on, demonstrating the cascading effect.
          </p>
          <p>
            <strong className="text-red-300">More O₂ → cyanobacteria lose advantage.</strong> Microcystis float when
            still; mixing destroys that advantage. The model uses Visser 1996's ε critical = 5×10⁻⁷ W/kg.
            When the live aeration ε passes this threshold, cyanobacteria growth rate is multiplied by a
            buoyancy-disadvantage factor (~0.3), letting diatoms/greens dominate. The reservoir shifts from
            cyano-dominated to a healthier mixed community.
          </p>
          <p>
            <strong className="text-violet-300">Net effect — Water Quality Index.</strong> The composite WQI
            (Pesce & Wunderlin 2000) weighs DO, BOD, NH₄, chl-a (with extra cyanobacteria penalty), and
            turbidity. Move the slider on this page and watch the WQI converge to its aerated steady state —
            this is the headline figure to take to the client.
          </p>
        </div>
      </div>
    </div>
  );
}
